import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { History as HistoryIcon, Layers, RefreshCw, Search, User, UserPlus } from 'lucide-react';
import { ROSTER_ROLES, ROSTER_ROLE_LABEL, TEAM_SIZE, formatDay, todayUk } from '@nu/shared';
import { api } from '../../api/client.js';
import { problem, teamColours } from './util.js';
import { AddPersonModal, BandsModal, HistoryModal, MoveModal, NewTeamModal, PersonTargetModal, RemoveModal, TeamModal } from './modals.jsx';

// Match-Ups: one row per person, like the NFUK Match-Ups table. Team,
// band, targets, team leads, van and home are columns, edited in place.
// Office only, online only.
const NEW_TEAM = '__new__';
// One colour per team (by creation order, so it doesn't change as you
// filter), used to frame its two engineers' rows together.
const LEADS = [
  { key: 'supervisorId', role: 'supervisor', label: 'Supervisor' },
  { key: 'jobControllerId', role: 'job_controller', label: 'Job Controller' },
  { key: 'operationsId', role: 'operations', label: 'Operations' },
];

export default function MatchUps({ me }) {
  const [day, setDay] = useState(todayUk);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState({});
  const [filter, setFilter] = useState('');
  const [role, setRole] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState(null); // { kind, ...props }

  const isToday = day === todayUk();
  // Past or future dates are for looking; changes are made on today's view.
  const canEdit = me.capabilities.includes('page.matchups.edit') && isToday;

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setData(await api(`/matchups?day=${day}`));
    } catch (e) {
      setLoadError(problem(e));
    }
  }, [day]);
  useEffect(() => { load(); }, [load]);

  // Run a change, say what happened, reload. Returns true on success so a
  // modal knows whether to close.
  const change = useCallback(async (fn, okText, key) => {
    setNotice(null);
    if (key) setBusy((b) => ({ ...b, [key]: true }));
    try {
      await fn();
      if (okText) setNotice({ tone: 'ok', text: okText });
      await load();
      return true;
    } catch (e) {
      setNotice({ tone: 'bad', text: problem(e) });
      return false;
    } finally {
      if (key) setBusy((b) => ({ ...b, [key]: false }));
    }
  }, [load]);

  const lookups = useMemo(() => data && {
    people: new Map(data.people.map((p) => [p.id, p])),
    teams: new Map(data.teams.map((t) => [t.id, t])),
    bands: new Map(data.bands.map((b) => [b.id, b])),
  }, [data]);

  const rows = useMemo(() => {
    if (!data) return [];
    const f = filter.trim().toLowerCase();
    const order = Object.fromEntries(ROSTER_ROLES.map((r, i) => [r.value, i]));
    return data.people
      .filter((p) => (showInactive || p.active)
        && (!role || p.role === role)
        && (!teamFilter || String(p.teamId) === teamFilter)
        && (!f || p.name.toLowerCase().includes(f) || p.email.includes(f) || (p.vanReg || '').toLowerCase().includes(f)))
      .sort((a, b) => order[a.role] - order[b.role]
        || (lookups.teams.get(a.teamId)?.name || '~').localeCompare(lookups.teams.get(b.teamId)?.name || '~')
        || a.name.localeCompare(b.name));
  }, [data, lookups, filter, role, teamFilter, showInactive]);

  const savePerson = (p, body, okText) => change(() => api(`/matchups/people/${p.id}`, { method: 'PUT', body }), okText, `${p.id}:${Object.keys(body)[0]}`);
  const saveTeam = (t, body, okText, key) => change(() => api(`/matchups/teams/${t.id}`, { method: 'PUT', body }), okText, key);

  const onTeamPick = (p, value) => {
    if (value === NEW_TEAM) return setModal({ kind: 'newTeam', person: p });
    if (!value) return setModal({ kind: 'remove', person: p, team: lookups.teams.get(p.teamId) });
    return setModal({ kind: 'move', person: p, team: lookups.teams.get(Number(value)), from: lookups.teams.get(p.teamId) });
  };

  // Pull in anyone given access since the hourly sync.
  const refresh = () => change(async () => {
    const r = await api('/matchups/sync', { method: 'POST' });
    const said = [r.added && `${r.added} added`, r.deactivated && `${r.deactivated} marked inactive`, r.reactivated && `${r.reactivated} reactivated`].filter(Boolean);
    setNotice({ tone: 'ok', text: said.length ? `Updated from Microsoft 365: ${said.join(', ')}.` : 'Already up to date with Microsoft 365.' });
  }, null, 'sync');

  const inactiveCount = data ? data.people.filter((p) => !p.active).length : 0;
  const activeTeams = useMemo(() => (data ? data.teams.filter((t) => t.active) : []), [data]);
  const teamColour = useMemo(() => teamColours(data?.teams), [data]);

  // The line-up at a glance: teams, installers, and who isn't in a team.
  const summary = useMemo(() => {
    if (!data) return null;
    const installers = data.people.filter((p) => p.active && p.role === 'installer');
    return {
      teams: activeTeams.length,
      installers: installers.length,
      placed: installers.filter((p) => p.teamId).length,
      unassigned: installers.filter((p) => !p.teamId).sort((a, b) => a.name.localeCompare(b.name)),
      short: activeTeams.filter((t) => t.members.length < TEAM_SIZE),
    };
  }, [data, activeTeams]);

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Match-Ups</h1>
            <p className="muted">Everyone in the operation: their role, team, band and targets. A team is {TEAM_SIZE} installers, one van each; its target is their targets added together.</p>
            <p className="muted small">People given Nano Utilities access appear here on their own (checked hourly); anyone who loses access is marked inactive.</p>
          </div>
          <div className="head-actions">
            <button type="button" className="btn with-icon" onClick={() => setModal({ kind: 'history' })}><HistoryIcon size={18} aria-hidden="true" /> History</button>
            {data && <button type="button" className="btn with-icon" onClick={() => setModal({ kind: 'bands' })}><Layers size={18} aria-hidden="true" /> Bands</button>}
            {canEdit && data && <button type="button" className="btn with-icon" disabled={busy.sync} onClick={refresh} title="People in the NU access groups appear here on their own every hour. This checks now."><RefreshCw size={18} aria-hidden="true" className={busy.sync ? 'spin' : undefined} /> Refresh</button>}
            {canEdit && data && <button type="button" className="btn primary with-icon" onClick={() => setModal({ kind: 'add' })}><UserPlus size={18} aria-hidden="true" /> Add person</button>}
          </div>
        </div>
        {notice && <p className={`notice-line notice-${notice.tone}`} role="status">{notice.text}</p>}
        {!me.capabilities.includes('page.matchups.edit') && <p className="muted small">You can view Match-Ups. Changes are made by the office.</p>}
      </section>

      {summary && (
        <section className="card">
          <div className="stat-row">
            <div className="stat"><span className="stat-num">{summary.teams}</span><span className="stat-label">Team{summary.teams === 1 ? '' : 's'}</span></div>
            <div className="stat"><span className="stat-num">{summary.installers}</span><span className="stat-label">Installers</span></div>
            <div className="stat"><span className="stat-num">{summary.placed}</span><span className="stat-label">In a team</span></div>
            <div className={`stat${summary.unassigned.length ? ' stat-warn' : ''}`}><span className="stat-num">{summary.unassigned.length}</span><span className="stat-label">Unassigned installers</span></div>
            <div className={`stat${summary.short.length ? ' stat-warn' : ''}`}><span className="stat-num">{summary.short.length}</span><span className="stat-label">Teams short of an engineer</span></div>
          </div>
          {summary.unassigned.length > 0 && (
            <p className="small stat-detail">
              <strong>Not in a team:</strong>{' '}
              {summary.unassigned.map((p, i) => (
                <span key={p.id}>{i > 0 && ', '}{p.name}{p.leave ? ' (on holiday)' : ''}</span>
              ))}
              {canEdit && '. Pick a team for them in the People table below.'}
            </p>
          )}
          {summary.short.length > 0 && (
            <p className="small stat-detail"><strong>Short:</strong> {summary.short.map((t) => `${t.name} (${t.members.length} of ${TEAM_SIZE})`).join(', ')}</p>
          )}
        </section>
      )}

      {data && activeTeams.length > 0 && (
        <section className="card">
          <h2 className="section-title">Teams</h2>
          <div className="table-scroll">
            <table className="table roster teams-table">
              <thead>
                <tr><th>Team</th><th>Engineers</th><th>Team target</th>{LEADS.map((l) => <th key={l.key}>{l.label}</th>)}</tr>
              </thead>
              <tbody>
                {activeTeams.map((team) => (
                  <tr key={team.id} className="team-row team-start team-end" style={{ '--team': teamColour.get(team.id) }}>
                    <td data-label="Team"><strong>{team.name}</strong></td>
                    <td data-label="Engineers">
                      {team.members.length
                        ? <ul className="crew">{team.members.map((m) => <li key={m.personId}><User size={13} aria-hidden="true" />{lookups.people.get(m.personId)?.name}</li>)}</ul>
                        : <span className="warn-text small">Nobody in this team</span>}
                      {team.members.length === 1 && <div className="warn-text small">Needs a partner</div>}
                    </td>
                    <td data-label="Team target">
                      <button type="button" className={`cell-btn${team.target.source === 'override' ? ' overridden' : ''}`} disabled={!canEdit || !isToday}
                        title={team.target.source === 'override' ? `Override: ${team.targetOverrideReason}` : 'Its engineers’ targets added together'}
                        onClick={() => setModal({ kind: 'team', team })}>
                        {team.target.value ?? '–'}
                        {team.target.source === 'override' && <span className="small"> override</span>}
                      </button>
                    </td>
                    {LEADS.map((l) => (
                      <td key={l.key} data-label={l.label}>
                        {canEdit && isToday ? (
                          <select value={team[l.key] || ''} disabled={busy[`${team.id}:${l.key}`]} aria-label={`${l.label} for ${team.name}`}
                            onChange={(e) => saveTeam(team, { [l.key]: e.target.value || null }, `${team.name}'s ${l.label.toLowerCase()} saved.`, `${team.id}:${l.key}`)}>
                            <option value="">None</option>
                            {data.people.filter((x) => x.role === l.role && (x.active || x.id === team[l.key])).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                          </select>
                        ) : (lookups.people.get(team[l.key])?.name || <span className="muted">None</span>)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="card">
        <h2 className="section-title">People</h2>
        <div className="filters">
          <label className="search">
            <Search size={18} aria-hidden="true" />
            <input type="search" placeholder="Filter by name, email or van" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </label>
          <select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Role">
            <option value="">All roles</option>
            {ROSTER_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} aria-label="Team">
            <option value="">All teams</option>
            {data?.teams.map((t) => <option key={t.id} value={t.id}>{t.name}{t.active ? '' : ' (closed)'}</option>)}
          </select>
          <label className="day-pick inline">
            <span className="small muted">As on</span>
            <input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} />
          </label>
        </div>
        {!isToday && (
          <p className="small filter-line">
            Showing the line-up on {formatDay(day)}. Changes are made on today&apos;s view.{' '}
            <button type="button" className="link-btn" onClick={() => setDay(todayUk())}>Back to today</button>
          </p>
        )}

        {loadError && (
          <div className="empty"><p>{loadError}</p><button type="button" className="btn" onClick={load}>Try again</button></div>
        )}
        {!loadError && !data && <p className="muted">Loading…</p>}
        {data && !data.people.length && <p className="muted">Nobody in Match-Ups yet.{canEdit ? ' Use Add person.' : ''}</p>}
        {data && data.people.length > 0 && !rows.length && <p className="muted">Nobody matches that filter.</p>}

        {rows.length > 0 && (
          <div className="table-scroll">
            <table className="table roster">
              <thead>
                <tr>
                  <th>Name</th><th>Role</th><th>Team</th><th>Partner</th><th>Band</th>
                  <th>Daily target</th>
                  <th>Van</th><th>Home postcode</th><th>In team since</th><th>Active</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p, i) => {
                  const team = p.teamId ? lookups.teams.get(p.teamId) : null;
                  // Frame a team's rows together: colour edge, top on its first row, bottom on its last.
                  const groupStart = team && rows[i - 1]?.teamId !== p.teamId;
                  const groupEnd = team && rows[i + 1]?.teamId !== p.teamId;
                  const teamClass = team ? ` team-row${groupStart ? ' team-start' : ''}${groupEnd ? ' team-end' : ''}` : '';
                  const member = team?.members.find((m) => m.personId === p.id);
                  const partner = team?.members.filter((m) => m.personId !== p.id).map((m) => lookups.people.get(m.personId)?.name).join(', ');
                  const installer = p.role === 'installer';
                  const edit = canEdit && p.active;
                  const na = <span className="muted">–</span>;
                  return (
                    <tr key={p.id} className={`${p.active ? '' : 'inactive'}${teamClass}`} style={team ? { '--team': teamColour.get(team.id) } : undefined}>
                      <td data-label="Name">
                        <strong>{p.name}</strong>{p.accessRemoved && <span className="tag" title="Removed from the NU access groups">No access</span>}
                        {p.leave && <span className="tag tag-leave" title="Holiday booked in BrightHR">{p.leave === 'all' ? 'On holiday' : `Holiday ${p.leave.toUpperCase()}`}</span>}
                        <div className="muted small">{p.email}</div>
                      </td>
                      <td data-label="Role">
                        {edit ? (
                          <select value={p.role} disabled={busy[`${p.id}:role`]} aria-label={`Role for ${p.name}`}
                            onChange={(e) => savePerson(p, { role: e.target.value }, `${p.name} is now ${ROSTER_ROLE_LABEL[e.target.value]}.`)}>
                            {ROSTER_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        ) : ROSTER_ROLE_LABEL[p.role]}
                      </td>
                      <td data-label="Team">
                        {!installer ? na : edit ? (
                          <select value={p.teamId ?? ''} aria-label={`Team for ${p.name}`} onChange={(e) => onTeamPick(p, e.target.value)}>
                            <option value="">Not in a team</option>
                            {activeTeams.map((t) => (
                              <option key={t.id} value={t.id} disabled={t.id !== p.teamId && t.members.length >= TEAM_SIZE}>
                                {t.name}{t.id !== p.teamId && t.members.length >= TEAM_SIZE ? ' (full)' : ''}
                              </option>
                            ))}
                            <option value={NEW_TEAM}>+ New team…</option>
                          </select>
                        ) : (team?.name || <span className="muted">Not in a team</span>)}
                      </td>
                      <td data-label="Partner">{installer && team ? (partner || <span className="warn-text small">Needs a partner</span>) : na}</td>
                      <td data-label="Band">
                        {!installer ? na : edit ? (
                          <select value={p.bandId ?? ''} disabled={busy[`${p.id}:bandId`]} aria-label={`Band for ${p.name}`}
                            onChange={(e) => savePerson(p, { bandId: e.target.value ? Number(e.target.value) : null }, `${p.name}'s band saved.`)}>
                            <option value="">No band</option>
                            {data.bands.filter((b) => b.active || b.id === p.bandId).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                          </select>
                        ) : (lookups.bands.get(p.bandId)?.name || <span className="muted">No band</span>)}
                      </td>
                      <td data-label="Daily target">
                        {!installer ? na : (
                          <button type="button" className={`cell-btn${p.target.source === 'override' ? ' overridden' : ''}`} disabled={!edit}
                            title={p.target.source === 'override' ? `Override: ${p.targetOverrideReason}` : 'From their band'}
                            onClick={() => setModal({ kind: 'personTarget', person: p })}>
                            {p.target.value ?? '–'}
                            {p.target.source === 'override' && <span className="small"> override</span>}
                          </button>
                        )}
                      </td>
                      <td data-label="Van">
                        {!installer ? na : p.van ? <Link to="/fleet/vans" className="van-link" title="Set in Fleet > Vans">{p.van.registration}</Link> : <TextCell value={p.vanReg} edit={edit} busy={busy[`${p.id}:vanReg`]} label={`Van for ${p.name}`} max={8}
                          onSave={(v) => savePerson(p, { vanReg: v }, `${p.name}'s van saved.`)} />}
                      </td>
                      <td data-label="Home postcode">
                        {!installer ? na : <TextCell value={p.homePostcode} edit={edit} busy={busy[`${p.id}:homePostcode`]} label={`Home postcode for ${p.name}`} max={8}
                          onSave={(v) => savePerson(p, { homePostcode: v }, `${p.name}'s home postcode saved.`)} />}
                      </td>
                      <td data-label="In team since">{member ? formatDay(member.fromDate) : na}</td>
                      <td data-label="Active">
                        <input type="checkbox" role="switch" className="switch" checked={p.active} disabled={!canEdit || busy[`${p.id}:active`]} aria-label={`${p.name} active`}
                          onChange={(e) => savePerson(p, { active: e.target.checked }, `${p.name} is now ${e.target.checked ? 'active' : 'inactive'}.`)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {inactiveCount > 0 && (
          <button type="button" className="link-btn small" onClick={() => setShowInactive((s) => !s)}>
            {showInactive ? 'Hide inactive people' : `Show inactive people (${inactiveCount})`}
          </button>
        )}
      </section>

      {modal?.kind === 'add' && <AddPersonModal bands={data.bands} change={change} onClose={() => setModal(null)} />}
      {modal?.kind === 'bands' && <BandsModal data={data} canEdit={canEdit} change={change} onClose={() => setModal(null)} />}
      {modal?.kind === 'history' && <HistoryModal onClose={() => setModal(null)} />}
      {modal?.kind === 'personTarget' && <PersonTargetModal person={modal.person} bands={lookups.bands} change={change} onClose={() => setModal(null)} />}
      {modal?.kind === 'team' && <TeamModal team={lookups.teams.get(modal.team.id)} lookups={lookups} change={change} onClose={() => setModal(null)} />}
      {modal?.kind === 'move' && <MoveModal {...modal} change={change} onClose={() => setModal(null)} />}
      {modal?.kind === 'remove' && <RemoveModal {...modal} change={change} onClose={() => setModal(null)} />}
      {modal?.kind === 'newTeam' && <NewTeamModal person={modal.person} change={change} onClose={() => setModal(null)} />}
    </div>
  );
}

// Text typed into a cell; saves when you leave the box or press Enter, only
// if it changed. Escape puts it back.
function TextCell({ value, edit, busy, label, max, onSave }) {
  const [v, setV] = useState(value || '');
  useEffect(() => { setV(value || ''); }, [value]);
  if (!edit) return value || <span className="muted">None</span>;
  const commit = () => { if (v.trim().toUpperCase() !== (value || '')) onSave(v.trim() || null); };
  return (
    <input className="cell-input" value={v} maxLength={max} disabled={busy} aria-label={label} autoCapitalize="characters"
      onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setV(value || ''); }} />
  );
}
